/*
 * This file contains the floorplan list card component.
 */

import clsx from "clsx";
import styles from "../floorplans.module.scss";

import { formatCurrency, getAvailabilityDate } from "../../../utils/util";
import FloorplanSpecialComponent from "./floorplanSpecialComponent";
import VirtualTourPopup from "../virtualTourPopup";
import FeeCalculatorPopup from "../feeCalculatorPopup";
import MoveInDatePicker from "../moveinDateModal/moveinDateModal";
import { useState } from "react";
import { traceEvent, traceFirstMeaningfulAction } from "../../../utils/tracing";

export default function FloorPlanListCard({
  data,
  fpData,
  specials,
  contactLink,
  params,
  typeListing,
  hasAnyVirtualTour,
  unitAmenities,
  engrainFeeCalculator,
  feeCalculatorUrl,
  moveInStartDate,
}) {
  const [isVirtualTourOpen, setIsVirtualTourOpen] = useState(false);
  const [isLeaseTermsOpen, setIsLeaseTermsOpen] = useState(false);
  const [isAmenitiesOpen, setIsAmenitiesOpen] = useState(false);
  const [isFeeCalculatorOpen, setIsFeeCalculatorOpen] = useState(false);

  // Construct fee calculator URL with marketing name and mode parameter
  const constructFeeCalculatorUrl = () => {
    if (!feeCalculatorUrl) return '';
    const url = new URL(feeCalculatorUrl);
    if (data?.marketing_name) {
      url.searchParams.set('unit_number', data.marketing_name);
    }
    url.searchParams.set('mode', 'expense_calculator');
    return url.toString();
  };

  return (
    <div className={styles.FloorplanListCard}>
      <FloorplanSpecialComponent
        typeListing={false}
        specials={specials}
        fpId={fpData?.floorplan_id}
      />
      <h3 className={styles.Name}>{data?.marketing_name}</h3>
      {/* Show floor number for unit listing */}
      <div className={styles.TwoLineBox}>
        Floor
        <span>
          {data?.floor_number}
        </span>
      </div>
      <div className={styles.TwoLineBox}>
        {getAvailabilityDate(data?.availability_date, data?.unit_status) !== "" ? (
          <>
            Available
            <span>
              {getAvailabilityDate(data?.availability_date, data?.unit_status)}
            </span>
          </>
        ) : (
          <span className={styles.ComingSoon}>Coming Soon</span>
        )}
      </div>
      <div className={styles.TwoLineBox}>
      {engrainFeeCalculator?.enabled === "1" && data?.engrain ? "Starting At" : "Price"}
        <span>
          {data?.best_price !== null && parseInt(data?.best_price) > 0 ? (
            <>
              {engrainFeeCalculator?.enabled === "1" && data?.engrain ? (
                <div className={styles.EngrainPriceWrapper}>
                      <span className={styles.EngrainPrice}>{
                    String(data?.engrain).includes(' - ') 
                      ? String(data?.engrain).split(' - ').map(val => formatCurrency(parseFloat(val))).join(' - ')
                      : formatCurrency(parseFloat(data?.engrain))
                  }*

                  <button 
                    className={styles.CalculatorIconBtn}
                    onClick={() => setIsFeeCalculatorOpen(true)}
                    aria-label="Open Fee Calculator"
                  >
                    <svg aria-hidden="true" className={styles.CalculatorIcon} width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" role="presentation">
                      <path fillRule="evenodd" clipRule="evenodd" d="M12 2.66665H3.99996V13.3333H12V2.66665ZM3.99996 1.33331C3.26358 1.33331 2.66663 1.93027 2.66663 2.66665V13.3333C2.66663 14.0697 3.26358 14.6666 3.99996 14.6666H12C12.7363 14.6666 13.3333 14.0697 13.3333 13.3333V2.66665C13.3333 1.93027 12.7363 1.33331 12 1.33331H3.99996Z"></path>
                      <path fillRule="evenodd" clipRule="evenodd" d="M12 6.66665H3.99996V5.33331H12V6.66665Z"></path>
                      <path fillRule="evenodd" clipRule="evenodd" d="M12 9.33331H3.99996V7.99998H12V9.33331Z"></path>
                      <path fillRule="evenodd" clipRule="evenodd" d="M9.99996 12H3.99996V10.6666H9.99996V12Z"></path>
                      <path fillRule="evenodd" clipRule="evenodd" d="M10.6666 5.99998L10.6666 14H9.33329L9.33329 5.99998H10.6666Z"></path>
                      <path fillRule="evenodd" clipRule="evenodd" d="M7.33329 5.99998V14H5.99996L5.99996 5.99998H7.33329Z"></path>
                    </svg>
                  </button>

                  </span>

     
                </div>
              ) : (
                <>
                  From <span>{formatCurrency(data?.best_price)}</span>
                </>
              )}
            </>
          ) : (
            <span>Call for pricing</span>
          )}
        </span>
      </div>
      <div className={styles.TwoLineBox}>
        Square Footage{" "}
        <span>{data?.sqft} SQ.FT.</span>
      </div>

      {getAvailabilityDate(data?.availability_date, data?.unit_status) !== "" ? (
        <div className={clsx(styles.BtnWrapper, "list-card-btn-wrapper", styles.unitBtnWrapper)}>
          {data?.best_price !== null && parseInt(data?.best_price) > 0 ? (
            <a
              href={`${process.env.REACT_APP_FP_LEASE_TERM_URL}/property[id]/${data?.apartment_id
                }/property_floorplan[id]/${fpData?.floorplan_id}/unit_space[id]/${data?.unit_space_id
                }/show_in_popup/false/from_check_availability/1/?lease_start_date=${new Date() > new Date(data?.availability_date)
                  ? new Date().toISOString().split('T')[0]
                  : data?.availability_date
                }`}
              target="_blank"
              rel="noreferrer"
              className={clsx(styles.Btn, styles.primary, "apply-now-btn")}
              onClick={() => {
                traceFirstMeaningfulAction();
                traceEvent(
                  typeListing ? "FLOORPLAN_APPLY_NOW_LANDING" : "FLOORPLAN_UNIT_LISTING_APPLY_NOW"
                );
              }}
            >
              Apply Now
            </a>
          ) : (
            <a href={contactLink} className={clsx(styles.Btn, styles.primary)}>
              Get Notified
            </a>
          )}
          {data?.best_price !== null && parseInt(data?.best_price) > 0 ? (
            <button
              className={clsx(styles.Btn, styles.secondary)}
              onClick={() => {
                traceEvent("LEASE_TERMS_BTN");
                setIsLeaseTermsOpen(true);
              }}
            >
              Lease Terms
            </button>
          ) : (
            <a
              href={contactLink}
              className={clsx(styles.Btn, styles.secondary)}
            >
              Inquire For Details
            </a>
          )}
          {hasAnyVirtualTour && data?.virtual_tour_url && (
            <button 
              className={clsx(styles.Btn, styles.VirtualTourBtn)}
              onClick={() => setIsVirtualTourOpen(true)}
            >
              Virtual Tour <svg xmlns="http://www.w3.org/2000/svg" width="18" height="12" viewBox="0 0 18 12" fill="none">
                <path d="M8.78662 0.121094C4.09462 0.121094 0.286621 2.01648 0.286621 4.35186C0.286621 6.24725 2.78562 7.84648 6.23662 8.38802V11.1211L9.63662 7.73648L6.23662 4.35186V6.66186C3.55912 6.18802 1.98662 5.05417 1.98662 4.35186C1.98662 3.45494 4.57062 1.8134 8.78662 1.8134C13.0026 1.8134 15.5866 3.45494 15.5866 4.35186C15.5866 4.96956 14.3456 5.95109 12.1866 6.49263V8.22725C15.1871 7.57571 17.2866 6.08648 17.2866 4.35186C17.2866 2.01648 13.4786 0.121094 8.78662 0.121094Z" fill="#AD8E6F" />
              </svg>
            </button>
          )}
        </div>
      ) : (
        <div className={clsx(styles.BtnWrapper, "list-card-btn-wrapper")}>
          <a
            href={contactLink}
            className={clsx(styles.Btn, styles.primary)}
          >
            Get Notified
          </a>
          <a
            href={contactLink}
            className={clsx(
              styles.Btn,
              styles.secondary,
              styles.fullWidthBtn,
              styles.pricingBtn
            )}
          >
            Inquire For Details
          </a>
        </div>
      )}
      <VirtualTourPopup
        isOpen={isVirtualTourOpen}
        onClose={() => setIsVirtualTourOpen(false)}
        virtualTourUrl={data?.virtual_tour_url}
      />
      <FeeCalculatorPopup
        isOpen={isFeeCalculatorOpen}
        onClose={() => setIsFeeCalculatorOpen(false)}
        feeCalculatorUrl={constructFeeCalculatorUrl()}
      />
      <MoveInDatePicker
        isOpen={isLeaseTermsOpen}
        onClose={() => setIsLeaseTermsOpen(false)}
        floorplanId={fpData?.floorplan_id}
        unitId={data?.unit_number}
        moveInStartDate={moveInStartDate}
        allowedDate={data?.availability_date}
      />
      {/* Unit Amenities Accordion */}
      {unitAmenities && unitAmenities.length > 0 && unitAmenities.some(amenity =>
        amenity?.amenities_items && amenity.amenities_items.length > 0
      ) && (
          <div className={styles.UnitAmenitiesAccordion}>
            <div
              className={clsx(styles.AccordionContent, isAmenitiesOpen && styles.open)}
            >
              <div className={styles.AccordionInner}>
                <h4>Additional Amenities</h4>
                <ul>
                  {unitAmenities?.map((amenity, index) =>
                    amenity?.amenities_items
                      ?.split(/,\s*\n|\n/)
                      .filter((item) => item.trim() !== "")
                      .map((item, itemIndex) => (
                        <li key={`${index}-${itemIndex}`}>
                          <svg width="6" height="6" viewBox="0 0 6 6" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="3" cy="3" r="2" stroke="#AD8E6F"></circle></svg>
                          <span>{item.trim()}</span>
                        </li>
                      ))
                  )}
                </ul>
              </div>
            </div>
            <button
              className={styles.AccordionToggle}
              onClick={() => setIsAmenitiesOpen(!isAmenitiesOpen)}
            >
              {isAmenitiesOpen ? "Hide Amenities" : "Show Amenities"}
              <span className={clsx(styles.AccordionIcon, isAmenitiesOpen && styles.open)}>
                <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </button>
          </div>
        )}
    </div>
  );
}
